<template>
  <v-card>
    <v-card-title>
      <span class="text-h5"
        >{{ $t('ITEM.CREATE') }} {{ $t('PERSONS.ITEM') }}</span
      >
    </v-card-title>
    <v-card-text>
      <v-sheet class="mt-5 pa-5" tile>
        <div v-if="error">
          <v-alert
            v-for="(e, index) in error.data.errors"
            :key="index"
            color="error"
            dark
            dense
          >
            {{ e[0] }}
          </v-alert>
        </div>

        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="validate"
        >
          <v-container grid-list-xl>
            <v-layout wrap>
              <v-flex xs12 md4>
                <v-select
                  v-model="person.salutation"
                  :items="salutations"
                  :label="$t('PROFILE.SALUTATION')"
                ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-text-field
                  v-model="person.first_name"
                  :rules="rules.requiredTextLine"
                  :label="`${$t('PROFILE.FIRST_NAME')} *`"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 md4>
                <v-text-field
                  v-model="person.last_name"
                  :rules="rules.requiredTextLine"
                  :label="`${$t('PROFILE.LAST_NAME')} *`"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 md4>
                <v-select
                  v-model="person.gender"
                  :items="genders"
                  :label="$t('PROFILE.GENDER')"
                ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-select
                  v-model="profile.marital_status"
                  :items="maritalStatuses"
                  :label="$t('PROFILE.MARITAL_STATUS')"
                ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="profile.birthday | birthDateFormatted"
                      :label="$t('PROFILE.BIRTH_DATE')"
                      append-icon="mdi-calendar"
                      readonly
                      clearable
                      @click:clear="clear"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    ref="picker"
                    v-model="profile.birthday"
                    :max="new Date().toISOString().substr(0, 10)"
                    min="1940-01-01"
                    first-day-of-week="1"
                    @change="save"
                  ></v-date-picker>
                </v-menu>
              </v-flex>

              <v-flex xs12 md4>
                <phone-input
                  v-model="profile.mobile_phone"
                  :label="$t('PROFILE.PHONE') + ' 1'"
                ></phone-input>
              </v-flex>

              <v-flex xs12 md4>
                <phone-input
                  v-model="profile.phone"
                  :label="$t('PROFILE.PHONE') + ' 1'"
                ></phone-input>
              </v-flex>

              <v-flex xs12 md4>
                <v-select
                  v-model="person.language_id"
                  :items="languages"
                  item-value="id"
                  item-text="name"
                  :label="`${$t('PROFILE.LANGUAGE')}`"
                ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-text-field
                  v-model="profile.street"
                  :label="$t('PROFILE.STREET')"
                ></v-text-field>
              </v-flex>

              <v-flex xs12 md4>
                <zip-code-input v-model="profile.zip" />
              </v-flex>

              <v-flex xs12 md4>
                <v-text-field
                  v-model="profile.city"
                  :label="$t('PROFILE.CITY')"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md12>
                <v-textarea
                  v-model="profile.add_info"
                  outlined
                  :rules="rules.textDescription"
                  :label="$t('PROFILE.ADDITIONAL_INFO')"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>

          <v-container>
            <v-layout align-center justify-space-between row>
              <v-btn outlined text @click="$emit('cancel')">{{
                $t('BUTTON.CANCEL')
              }}</v-btn>
              <v-btn
                color="success"
                :disabled="!valid"
                :loading="loading"
                @click="validate"
              >
                {{ $t('BUTTON.CREATE') }}
              </v-btn>
            </v-layout>
          </v-container>
        </v-form>
      </v-sheet>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment';
import { mask } from 'vue-the-mask';
import {
  requiredTextLine,
  textDescription
} from '@/modules/_global/variables/rules.js';
import ZipCodeInput from '@/modules/_global/_components/zip/ZipCodeInput';
import { mapState } from 'vuex';

export default {
  name: 'RelationsCreateNewPerson',

  directives: {
    mask
  },

  components: {
    ZipCodeInput,
    PhoneInput: () => import('@/modules/_global/_components/phone/PhoneInput')
  },

  filters: {
    birthDateFormatted(value) {
      if (!value) return '';

      return moment(value).format('DD.MM.YYYY');
    }
  },

  data: () => ({
    person: {
      terms_accepted: true,
      dsg_accepted: true
    },
    profile: {},
    error: null,
    menu: false,

    valid: true,
    rules: {
      requiredTextLine,
      textDescription
    },

    salutations: ['Mr.', 'Ms.', 'Mrs.'],
    genders: ['Male', 'Female'],
    maritalStatuses: ['Single', 'Married', 'Divorced', 'Widowed'],
    languages: []
  }),

  computed: {
    ...mapState(['loading'])
  },

  watch: {
    menu(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'));
    },
    menu_from(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'));
    }
  },

  created() {
    this.$store
      .dispatch('localization/retrieveLanguages')
      .then(response => {
        this.languages = response.data;
      })
      .catch(error => {
        this.error = error.response;
      });

    this.person.language_id = 1;
  },

  methods: {
    clear() {
      this.profile.birthday = null;
    },

    save(date) {
      this.$refs.menu.save(date);
    },

    saveDatefrom(date) {
      this.$refs.menuDateFrom.save(date);
    },

    validate() {
      if (this.$refs.form.validate()) {
        this.$store
          .dispatch('person/storeRelatedPerson', {
            person: this.person,
            personal_profile: this.profile
          })
          .then(response => {
            this.$store.commit('snackbar/setSnack', {
              status: 'success',
              text: this.$t('MESSAGE.SUCCESS')
            });
            this.$emit('success', response.data.data);
            this.$store.commit('loading', false);
          })
          .catch(error => {
            this.error = error.response;
            this.$store.commit('loading', false);
          });
      }
    }
  }
};
</script>
